// SharedWithMeSection — dashboard surface listing papers the current user
// has been invited into (collaborate or review intent).
//
// Backed by `useSharedWithMe` → `manage-review-invitation` `list_for_user`.
// This is a recipient-side view — we do NOT show invitation management
// controls here (resend/revoke live in the InviteDialog for owners).
import React from 'react';
import { Link } from 'react-router-dom';
import { FileSearch, UserPlus, Inbox } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useSharedWithMe } from '@/hooks/papers/useSharedWithMe';
import { cn } from '@/lib/utils';

const intentMeta = {
  collaborate: {
    label: 'Collaborator',
    Icon: UserPlus,
    className: 'bg-primary/10 text-primary border-primary/20',
  },
  review: {
    label: 'Review requested',
    Icon: FileSearch,
    className: 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/20',
  },
} as const;

const SharedWithMeSection: React.FC = () => {
  const { data, isLoading, error } = useSharedWithMe();

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-5 bg-muted rounded w-2/3" />
            </CardHeader>
            <CardContent>
              <div className="h-4 bg-muted rounded w-1/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-destructive py-4">
        Could not load shared papers. Please try again.
      </p>
    );
  }

  const rows = data?.rows ?? [];
  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Inbox className="h-12 w-12 text-muted-foreground mb-4" aria-hidden />
          <h3 className="text-lg font-semibold mb-2">Nothing shared with you yet</h3>
          <p className="text-muted-foreground text-center max-w-md text-sm">
            When someone invites you to view, comment on, or review a paper, it will
            appear here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((row) => {
        const meta = intentMeta[row.intent] ?? intentMeta.collaborate;
        const Icon = meta.Icon;
        return (
          <Card key={row.id} className="transition-shadow hover:shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-3">
                <CardTitle className="text-base truncate">
                  <Link
                    to={`/paper/${row.paper_id}`}
                    className="hover:underline"
                    title={row.paper_title ?? ''}
                  >
                    {row.paper_title ?? 'Untitled paper'}
                  </Link>
                </CardTitle>
                <Badge variant="outline" className={cn('text-[10px] gap-1', meta.className)}>
                  <Icon className="h-3 w-3" aria-hidden />
                  {meta.label}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              <p className="text-xs text-muted-foreground">
                Shared by {row.owner_name ?? 'a paper owner'} ·{' '}
                {new Date(row.created_at).toLocaleDateString()}
              </p>
              {row.personal_note && (
                <div className="bg-muted/50 border-l-2 border-muted-foreground/30 px-3 py-2 rounded-sm">
                  <p className="text-xs italic line-clamp-2">{row.personal_note}</p>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default SharedWithMeSection;
